import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { getAuth, sendEmailVerification, PhoneAuthProvider, linkWithCredential } from "firebase/auth";
import { sanitizeTurkishPhone, formatPhoneInput } from '../lib/phoneUtils';
import { requestPhoneOtp, logPhoneAuthEvent } from '../lib/phoneOtp';
import { getPhoneAuthErrorMessage, isRecaptchaEnvironmentError } from '../lib/recaptchaUtils';
import BrowserCompatNotice from '../components/BrowserCompatNotice';

const VerifyContactPage = () => {
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState(1); // 1: Enter Phone, 2: Verify OTP
    const [emailSent, setEmailSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showBrowserTip, setShowBrowserTip] = useState(false);
    const [statusText, setStatusText] = useState('');
    const [verificationId, setVerificationId] = useState(null);
    const recaptchaHostRef = useRef(null);
    const sendingRef = useRef(false); // guards against double taps re-using a token
    const { currentUser, userData, refreshUserData } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (userData?.isPhoneVerified && currentUser?.emailVerified) {
            navigate('/dashboard');
        }
    }, [userData, currentUser, navigate]);

    const handleSendVerificationEmail = async () => {
        try {
            setLoading(true);
            const auth = getAuth();
            await sendEmailVerification(auth.currentUser);
            setEmailSent(true);
            alert("Doğrulama e-postası gönderildi! Lütfen gelen kutunuzu kontrol edin.");
        } catch (err) {
            console.error(err);
            setError("E-posta gönderilemedi. Lütfen daha sonra tekrar deneyin.");
        } finally {
            setLoading(false);
        }
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        if (sendingRef.current) return;

        const sanitized = sanitizeTurkishPhone(phone);
        if (!sanitized.isValid) {
            setError(sanitized.error);
            return;
        }

        sendingRef.current = true;
        setLoading(true);
        setError('');
        setStatusText('Güvenlik kontrolü yapılıyor...');

        try {
            const vId = await requestPhoneOtp({
                phoneNumber: sanitized.e164,
                host: recaptchaHostRef.current,
                onAttempt: (n) => setStatusText(n > 1 ? 'Tekrar deneniyor...' : 'Güvenlik kontrolü yapılıyor...'),
            });
            setVerificationId(vId);
            setShowBrowserTip(false);
            setError('');
            setStep(2);
        } catch (err) {
            console.error("Phone OTP error:", err);
            setError(getPhoneAuthErrorMessage(err));
            if (isRecaptchaEnvironmentError(err)) setShowBrowserTip(true);
        } finally {
            sendingRef.current = false;
            setStatusText('');
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (!verificationId) {
            setError('Lütfen önce doğrulama kodu isteyin.');
            return;
        }
        const sanitized = sanitizeTurkishPhone(phone);
        setLoading(true);
        setError('');

        try {
            const auth = getAuth();
            const credential = PhoneAuthProvider.credential(verificationId, otp.trim());
            
            try {
                await linkWithCredential(auth.currentUser, credential);
            } catch (linkErr) {
                if (linkErr.code === 'auth/provider-already-linked' || linkErr.code === 'auth/credential-already-in-use') {
                    console.warn("Credential already linked, proceeding to update user document:", linkErr.code);
                } else {
                    throw linkErr;
                }
            }

            // Save formatted E.164 phone to Firestore
            const userRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userRef, {
                phone: sanitized.e164,
                isPhoneVerified: true
            });
            if (refreshUserData) {
                await refreshUserData();
            }
            alert('Telefon numaranız başarıyla doğrulandı!');
            navigate('/dashboard');
        } catch (err) {
            console.error('OTP verify error:', err);
            logPhoneAuthEvent('otp_verify_error', { code: err?.code || 'unknown', message: String(err?.message || '') });
            if (err.code === 'auth/invalid-verification-code') {
                setError('Hatalı doğrulama kodu. Lütfen SMS ile gelen 6 haneli kodu kontrol ediniz.');
            } else if (err.code === 'auth/code-expired') {
                setError('Doğrulama kodunun süresi doldu. Lütfen "Numarayı Değiştir" ile yeni kod isteyin.');
            } else {
                setError(`Doğrulama başarısız oldu: ${err.message || 'Lütfen tekrar deneyin.'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-10 px-4">
            <div className="w-full max-w-md bg-white shadow-lg rounded-xl p-8">
                <h2 className="text-2xl font-bold mb-6 text-center">Hesap Doğrulama</h2>

                {/* Email Verification Section */}
                <div className="mb-8 border-b pb-6">
                    <h3 className="text-lg font-semibold mb-2">E-posta Doğrulama</h3>
                    <p className="text-gray-600 text-sm mb-4">
                        {currentUser?.emailVerified
                            ? <span className="text-green-600 font-bold">✓ E-posta doğrulandı</span>
                            : "Hesabınızın güvenliği için e-posta adresinizi doğrulayın."}
                    </p>
                    {!currentUser?.emailVerified && (
                        <button
                            onClick={handleSendVerificationEmail}
                            disabled={emailSent || loading}
                            className={`text-sm px-4 py-2 rounded-lg border transition-colors ${emailSent ? 'bg-green-50 text-green-700 border-green-200' : 'bg-white text-black border-black hover:bg-gray-50'}`}
                        >
                            {emailSent ? 'E-posta Gönderildi' : 'Doğrulama E-postası Gönder'}
                        </button>
                    )}
                </div>

                {/* Phone Verification Section */}
                <div>
                    <h3 className="text-lg font-semibold mb-2">Telefon Doğrulama</h3>
                    {userData?.isPhoneVerified ? (
                        <p className="text-green-600 font-bold text-sm">✓ Telefon doğrulandı</p>
                    ) : (
                        <>
                            <p className="text-gray-600 text-sm mb-4">
                                QR kod bağlamak için telefon doğrulaması zorunludur.
                            </p>
                            {error && <p className="bg-red-100 text-red-700 p-3 rounded-lg text-sm mb-3">{error}</p>}
                            {step === 1 && <BrowserCompatNotice force={showBrowserTip} />}

                            {step === 1 ? (
                                <form onSubmit={handleSendOtp}>
                                    <p className="text-xs text-gray-500 mb-2">Başında 0 olmadan, 10 haneli numaranızı girin (örn: 5XX XXX XX XX)</p>
                                    <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden mb-3">
                                        <span className="px-3 py-2 bg-gray-100 text-gray-600 text-sm font-medium border-r border-gray-300">+90</span>
                                        <input
                                            type="tel"
                                            value={phone}
                                            onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                                            placeholder="5XX XXX XX XX"
                                            className="flex-1 px-3 py-2 focus:outline-none"
                                            required
                                        />
                                    </div>

                                    <button
                                        id="send-otp-btn"
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-black text-white py-2.5 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                                    >
                                        {loading ? (statusText || 'Gönderiliyor...') : 'Doğrulama Kodu Gönder'}
                                    </button>

                                    {/* Invisible reCAPTCHA host (fresh widget is created per attempt) */}
                                    <div ref={recaptchaHostRef} id="verify-recaptcha-container" className="flex justify-center mt-3"></div>
                                    <p className="text-[10px] text-gray-400 text-center mt-2 leading-snug">
                                        Bu form reCAPTCHA ile korunmaktadır. Google{' '}
                                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Gizlilik Politikası</a> ve{' '}
                                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Hizmet Şartları</a> geçerlidir.
                                    </p>
                                </form>
                            ) : (
                                <form onSubmit={handleVerifyOtp}>
                                    <p className="text-sm text-gray-600 mb-2 font-medium">{sanitizeTurkishPhone(phone).formatted || phone} numarasına gönderilen SMS kodunu girin:</p>
                                    <input
                                        type="text"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        placeholder="123456"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-3 tracking-widest text-center text-lg focus:outline-none focus:ring-2 focus:ring-black"
                                        maxLength={6}
                                        required
                                    />
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-black text-white py-2.5 rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors font-medium"
                                    >
                                        {loading ? 'Doğrulanıyor...' : 'Doğrula'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setStep(1); setOtp(''); setError(''); setVerificationId(null); }}
                                        className="w-full mt-2 text-sm text-gray-500 hover:text-black transition-colors"
                                    >
                                        Numarayı Değiştir
                                    </button>
                                </form>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyContactPage;